import { createContext, useContext, useEffect, useState } from 'react'
import { syncEngine, type SyncStatus, type SyncState } from '../lib/syncEngine'
import { sweepExpiredCache } from '../lib/offlineStore'

// ─── Context ─────────────────────────────────────────────────────

interface SyncContextValue {
  isOnline: boolean
  syncState: SyncState
  pendingCount: number
  lastSyncError?: string
  rejectedCount: number
  rejectedSummaries: string[]
  forceSync: () => Promise<void>
}

const SyncContext = createContext<SyncContextValue>({
  isOnline: true,
  syncState: 'idle',
  pendingCount: 0,
  rejectedCount: 0,
  rejectedSummaries: [],
  forceSync: async () => {},
})

// ─── Provider ────────────────────────────────────────────────────

export function SyncProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<SyncStatus>(syncEngine.getStatus())

  useEffect(() => {
    syncEngine.init()
    void sweepExpiredCache() // [LEGAL] purge stale cached PII on canvasser-shell mount
    const unsubscribe = syncEngine.subscribe(setStatus)
    return () => {
      unsubscribe()
      syncEngine.destroy()
    }
  }, [])

  const forceSync = async () => {
    await syncEngine.sync()
  }

  return (
    <SyncContext.Provider
      value={{
        isOnline: status.isOnline,
        syncState: status.syncState,
        pendingCount: status.pendingCount,
        lastSyncError: status.lastSyncError,
        rejectedCount: status.rejectedCount,
        rejectedSummaries: status.rejectedSummaries,
        forceSync,
      }}
    >
      {children}
    </SyncContext.Provider>
  )
}

// ─── Hook ────────────────────────────────────────────────────────

export function useSync() {
  return useContext(SyncContext)
}
